import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Seo, { SITE_URL, SITE_NAME } from "../components/Seo";
import { trackClick } from "../lib/analytics";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import ArticlePaywall from "../components/ArticlePaywall";
import ReadingProgress from "../components/ReadingProgress";
import RelatedArticles from "../components/RelatedArticles";
import AdminReaderPreview, { readerViewLabel } from "../components/AdminReaderPreview";
import { ArrowLeft, Clock, Eye } from "lucide-react";
import { categoryLabel } from "../lib/articleCategories";
import TierBadge from "../components/TierBadge";
import TableOfContents, { TocAside } from "../components/editor/TableOfContents";
import GlossaryHover from "../components/GlossaryHover";
import { linkGlossary } from "../lib/glossary";
import { useCoursesData } from "../lib/courseData";
import { applyInternalLinks, renderMath } from "../lib/content";
import { safeHtml } from "../lib/sanitize";
import { useInternalLinks } from "../lib/internalLinks";

/**
 * Texte de l'analyse (HTML de l'éditeur). Composant mémorisé : il n'est pas redessiné quand la page change d'état
 * (survol d'un mot, progression), pour garder les formules et les liens du glossaire.
 * Les liens internes reçoivent leur adresse à jour et s'ouvrent sans rechargement.
 * Mots du glossaire : comme dans les cours, la première apparition de chaque mot devient un lien (sauf ceux de « off »).
 */
const ArticleHtml = memo(function ArticleHtml({ html, hrefs, terms, off, onClick, onOver, onLeave }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    applyInternalLinks(ref.current, hrefs);
    if (terms.length) linkGlossary(ref.current, terms, { off });
    renderMath(ref.current);
  }, [html, hrefs, terms, off]);
  return <div ref={ref} dangerouslySetInnerHTML={{ __html: safeHtml(html) }} onClick={onClick} onMouseOver={onOver} onMouseLeave={onLeave} />;
});
const EMPTY = [];
// Renders content from Tiptap (HTML). Falls back to safe paragraph split for legacy markdown.
const looksLikeHtml = (content) => /<\/?[a-z][\s\S]*>/i.test(content || "");
function renderContent(content, hrefs, onClick, gloss) {
  if (!content) return null;
  if (looksLikeHtml(content)) {
    return <ArticleHtml html={content} hrefs={hrefs} onClick={onClick} {...gloss} />;
  }
  // Legacy markdown fallback (older articles)
  return content.split(/\n\n+/).map((block, idx) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={idx} className="font-display font-bold text-2xl mt-8 mb-3">
          {trimmed.slice(3)}
        </h2>
      );
    }
    if (trimmed.startsWith("# ")) {
      return (
        <h1 key={idx} className="font-display font-black text-3xl mt-10 mb-4">
          {trimmed.slice(2)}
        </h1>
      );
    }
    if (trimmed.startsWith("> ")) {
      return (
        <blockquote key={idx} className="border-l-2 border-emerald-500/60 pl-4 my-4 text-zinc-300 italic">
          {trimmed.slice(2)}
        </blockquote>
      );
    }
    const html = trimmed
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-white/5 text-emerald-300 text-sm">$1</code>')
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noreferrer" class="text-emerald-400 hover:text-emerald-300 underline">$1</a>',
      )
      .replace(/\n/g, "<br/>");
    return (
      <p key={idx} className="text-zinc-300 leading-relaxed my-4" dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />
    );
  });
}

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAdmin = !!(user && user.role === "admin");

  // Aperçu admin : "admin" = vue normale ; sinon simule un visiteur, un membre gratuit ou un abonné.
  const [previewAs, setPreviewAs] = useState("admin");
  const view = isAdmin ? previewAs : "admin";
  const previewParam =
    view === "subscriber" ? "subscriber" : view === "guest" || view === "free" ? "free" : null;

  const bodyRef = useRef(null);

  const navigate = useNavigate();
  const { hrefs } = useInternalLinks();
  // Définitions du glossaire (liens automatiques dans le texte) et définition affichée au survol
  const { courses } = useCoursesData();
  const terms = useMemo(() => courses.filter((c) => c.category === "vocabulary"), [courses]);
  const [hover, setHover] = useState(null);
  // Liens vers d'autres pages du site : navigation sans rechargement
  const onContentClick = useCallback((e) => {
    const href = e.target.closest("a")?.getAttribute("href") || "";
    if (href.startsWith("/") && !href.startsWith("//") && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      setHover(null);
      navigate(href);
    }
  }, [navigate]);
  const onContentLeave = useCallback(() => setHover(null), []);
  const onContentOver = useCallback((e) => {
    const a = e.target.closest("a.gloss");
    if (!a) { setHover(null); return; }
    const r = a.getBoundingClientRect();
    setHover({ title: a.getAttribute("data-gloss-title"), def: a.getAttribute("data-gloss-def"), x: r.left + r.width / 2, y: r.top, top: r.top, bottom: r.bottom });
  }, []);

  const { data: article, isLoading, isError } = useQuery({
    queryKey: ["article", slug, previewParam],
    queryFn: async () =>
      (await api.get(`/articles/${slug}`, { params: previewParam ? { preview_as: previewParam } : undefined })).data,
    retry: false,
  });

  useEffect(() => {
    if (article && article.is_premium) trackClick("premium_article_open");
  }, [article]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="h-8 w-2/3 bg-white/5 rounded animate-pulse mb-4" />
        <div className="h-4 w-1/3 bg-white/5 rounded animate-pulse mb-8" />
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-4 bg-white/5 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display font-black text-3xl mb-3">Article introuvable</h1>
        <p className="text-zinc-500 mb-6">
          Cet article a peut-être été retiré ou n'existe pas.
        </p>
        <Link to="/articles" className="text-emerald-400 hover:text-emerald-300 text-sm">
          ← Retour aux articles
        </Link>
      </div>
    );
  }

  const tier = article.tier_required;
  const isPreviewing = isAdmin && previewAs !== "admin";
  // Le lien « Se connecter » de l'encart n'apparaît que pour un visiteur non connecté (réel ou simulé)
  const loggedIn = isPreviewing ? previewAs !== "guest" : !!user;

  return (
    <article className="min-h-screen" data-testid="article-detail">
      <Seo
        title={article.title}
        description={article.excerpt || undefined}
        path={`/articles/${article.slug}`}
        image={article.cover_image || undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt || undefined,
          image: article.cover_image ? [article.cover_image] : undefined,
          datePublished: article.created_at || undefined,
          dateModified: article.updated_at || article.created_at || undefined,
          author: { "@type": "Person", name: article.author || SITE_NAME },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-sideb.png` },
          },
          mainEntityOfPage: `${SITE_URL}/articles/${article.slug}`,
        }}
      />
      <ReadingProgress targetRef={bodyRef} color="violet" />

      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-3xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : vous voyez l'analyse comme
              <b className="font-semibold text-white">{readerViewLabel(previewAs)}</b>
            </span>
            <button
              type="button"
              onClick={() => setPreviewAs("admin")}
              className="text-violet-300 hover:text-white underline-offset-4 hover:underline"
            >
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}

      <div className="border-b border-white/5">
        <div className="mx-auto max-w-3xl px-6 pt-7 pb-8 sm:py-12">
          {/* Lien retour : inutile sur téléphone (barre du bas) */}
          <Link
            to="/articles"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-emerald-400 mb-6"
          >
            <ArrowLeft className="w-3 h-3" /> Toutes les analyses
          </Link>
          <div className="text-[10px] uppercase tracking-[0.2em] text-violet-400 mb-3">
            {categoryLabel(article.category)}
          </div>
          <h1 className="font-display font-black text-[2rem] sm:text-5xl leading-tight">
            {article.title}
          </h1>
          {/* Chapô : seulement s'il a été écrit (sinon ce serait le début du texte, affiché deux fois) */}
          {article.excerpt && !article.excerpt_auto && (
            // whitespace-pre-line : les retours à la ligne tapés dans le chapô sont conservés
            <p className="mt-5 text-lg md:text-xl text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="article-lead">
              {article.excerpt}
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-zinc-500">
            <span>Par {article.author}</span>
            <span>·</span>
            <span>{article.created_at ? new Date(article.created_at).toLocaleDateString("fr-FR") : ""}</span>
            {article.reading_minutes ? (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1" data-testid="article-reading-time">
                  <Clock className="w-3.5 h-3.5" /> {article.reading_minutes} min de lecture
                </span>
              </>
            ) : null}
            {tier && tier !== "free" && (
              <span data-testid="article-tier-badge">
                <TierBadge tier="essentiel" locked={!!article.is_locked} />
              </span>
            )}
          </div>
        </div>
      </div>

      {article.cover_image && (
        <div className="mx-auto max-w-4xl px-6 mt-8">
          <div
            className="aspect-[16/9] rounded-xl border border-white/5"
            style={{
              backgroundImage: `url(${article.cover_image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>
      )}

      <div ref={bodyRef} className="relative mx-auto max-w-3xl px-6 pt-2 pb-4 sm:py-10">
        {/* Sommaire : en haut du texte ; sur grand écran (≥ 1280 px), fixé sur le côté gauche pendant la lecture */}
        {!article.is_locked && looksLikeHtml(article.content) && (
          <>
            <TableOfContents html={article.content} toc={article.toc} accent="violet" className="xl:hidden" floating raised={isAdmin} />
            <TocAside html={article.content} toc={article.toc} accent="violet" className="hidden xl:block" />
          </>
        )}
        <div className="article-content prose prose-invert prose-emerald md:prose-lg max-w-none" data-testid="article-content">
          {renderContent(article.content, hrefs, onContentClick, {
            terms,
            off: article.glossary_off || EMPTY,
            onOver: onContentOver,
            onLeave: onContentLeave,
          })}
        </div>

        {article.is_locked && <ArticlePaywall loggedIn={loggedIn} />}
      </div>

      <RelatedArticles currentSlug={article.slug} category={article.category} />

      <GlossaryHover hover={hover} />
      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} />}
    </article>
  );
}
